import { useState, useMemo, useEffect } from "react";
import { M1User, M2User } from "../../../mocks/users";
import { supabase } from "../../../lib/supabase";

interface StaffRow {
  id?: number;
  m_code: string;
  full_name: string;
  phone: string;
  email: string;
  password: string;
  status: string;
  created_at?: string;
}

interface M1TableProps {
  data: M1User[];
  onExport: () => void;
}

interface M2TableProps {
  data: M2User[];
  onExport: () => void;
}

const StatusBadge = ({ status }: { status: string }) => {
  const styles: Record<string, string> = {
    active: "bg-green-100 text-green-700",
    inactive: "bg-gray-100 text-gray-500",
    pending: "bg-amber-100 text-amber-700",
  };
  const labels: Record<string, string> = {
    active: "Hoạt động",
    inactive: "Vô hiệu",
    pending: "Chờ duyệt",
  };
  return (
    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${styles[status] ?? styles["inactive"]}`}>
      {labels[status] ?? status}
    </span>
  );
};

export const M1CodeTable: React.FC<M1TableProps> = ({ onExport }) => {
  const [search, setSearch] = useState("");
  const [staffList, setStaffList] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStaff = async () => {
      setLoading(true);
      const { data } = await supabase.from("staff").select("*").order("created_at", { ascending: true });
      setStaffList((data as StaffRow[]) ?? []);
      setLoading(false);
    };
    fetchStaff();
  }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return staffList.filter((u) =>
      !q || u.m_code.toLowerCase().includes(q) || u.full_name.toLowerCase().includes(q) ||
      (u.email ?? "").toLowerCase().includes(q) || (u.phone ?? "").includes(q)
    );
  }, [staffList, search]);

  const handleExport = () => {
    const bom = "\uFEFF";
    const headers = ["M-Code", "Họ tên", "Số điện thoại", "Email", "Trạng thái", "Ngày tạo"];
    const rows = staffList.map((u) => [u.m_code, u.full_name, u.phone, u.email, u.status === "active" ? "Hoạt động" : "Vô hiệu", u.created_at?.slice(0, 10) ?? ""]);
    const csvContent = bom + [headers, ...rows].map((row) => row.map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Bizwell_M1_NhanVien_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    onExport?.();
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-800 text-base">Danh Sách Nhân Viên M1 (Supabase)</h3>
          <p className="text-xs text-gray-400 mt-0.5">Tổng {filtered.length} / {staffList.length} nhân viên · Dữ liệu thật từ Supabase</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên, code, email..."
              className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-[#004A99] outline-none w-48"
            />
          </div>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line" />
            Xuất Excel
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 text-center"><i className="ri-loader-4-line text-2xl text-[#004A99] animate-spin block mb-2" /><p className="text-xs text-gray-400">Đang tải dữ liệu thật từ Supabase...</p></div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#F8F9FA] border-b border-gray-100">
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">M-Code</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Họ tên</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Số điện thoại</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Email</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Ngày tạo</th>
                <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Trạng thái</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map((staff) => (
                <tr key={staff.m_code} className="hover:bg-[#F8F9FA] transition-colors">
                  <td className="px-4 py-3">
                    <code className="font-mono font-bold text-[#004A99] bg-[#EDF2FB] px-2 py-0.5 rounded whitespace-nowrap">
                      {staff.m_code}
                    </code>
                    {staff.m_code === "M1-11-000" && (
                      <span className="ml-2 text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-semibold">Super Admin</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{staff.full_name}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap font-mono">{staff.phone || "—"}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{staff.email || "—"}</td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{staff.created_at?.slice(0, 10) ?? "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold whitespace-nowrap ${staff.status === "active" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"}`}>
                      {staff.status === "active" ? "Hoạt động" : "Vô hiệu"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="py-12 text-center text-gray-400 text-sm">
              <i className="ri-search-line text-2xl block mb-2" />
              Không tìm thấy kết quả
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const M2CodeTable: React.FC<M2TableProps> = ({ data, onExport }) => {
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState<"ALL" | "active" | "pending" | "inactive">("ALL");

  const filtered = useMemo(() => {
    return data.filter((u) => {
      const matchSearch =
        u.code.toLowerCase().includes(search.toLowerCase()) ||
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.phone.includes(search) ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        u.company.toLowerCase().includes(search.toLowerCase());
      const matchStatus = filterStatus === "ALL" || u.status === filterStatus;
      return matchSearch && matchStatus;
    });
  }, [data, search, filterStatus]);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div>
          <h3 className="font-bold text-gray-800 text-base">Danh Sách Khách Hàng (M2)</h3>
          <p className="text-xs text-gray-400 mt-0.5">Tổng {filtered.length} / {data.length} khách hàng</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-gray-100 rounded-lg p-0.5 text-xs font-semibold">
            {(["ALL", "active", "pending", "inactive"] as const).map((s) => (
              <button
                key={s}
                onClick={() => setFilterStatus(s)}
                className={`px-3 py-1.5 rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                  filterStatus === s ? "bg-white text-[#004A99]" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {s === "ALL" ? "Tất cả" : s === "active" ? "✓ Hoạt động" : s === "pending" ? "⏳ Chờ duyệt" : "✗ Vô hiệu"}
              </button>
            ))}
          </div>
          <div className="relative">
            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Tìm theo tên, code, SĐT..."
              className="pl-7 pr-3 py-1.5 text-xs border border-gray-200 rounded-lg focus:border-[#004A99] outline-none w-48"
            />
          </div>
          <button
            onClick={onExport}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-file-excel-2-line" />
            Xuất Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-[#F8F9FA] border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">M-Code</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Họ tên</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Điện thoại</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Mã đăng nhập</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Email</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Công ty</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Ngành nghề</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Thành phố</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Đăng ký</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Premium</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-500 whitespace-nowrap">Trạng thái</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {filtered.map((user) => (
              <tr key={user.code} className="hover:bg-[#F8F9FA] transition-colors">
                <td className="px-4 py-3">
                  <code className="font-mono font-bold text-[#004A99] bg-[#EDF2FB] px-2 py-0.5 rounded text-[10px] whitespace-nowrap">
                    {user.code}
                  </code>
                </td>
                <td className="px-4 py-3 font-semibold text-gray-800 whitespace-nowrap">{user.name}</td>
                <td className="px-4 py-3 text-gray-600 font-mono whitespace-nowrap">{user.phone}</td>
                <td className="px-4 py-3">
                  <code className="text-[10px] bg-amber-50 text-amber-700 px-2 py-0.5 rounded font-mono whitespace-nowrap border border-amber-200">
                    {user.loginKey}
                  </code>
                </td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{user.email || "—"}</td>
                <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{user.company || "—"}</td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{user.industry}</td>
                <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{user.city}</td>
                <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{user.registeredAt}</td>
                <td className="px-4 py-3">
                  {user.premium ? (
                    <span className="text-[10px] px-2 py-0.5 bg-[#004A99] text-white rounded-full font-semibold whitespace-nowrap">Premium</span>
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 bg-gray-100 text-gray-400 rounded-full font-semibold whitespace-nowrap">Free</span>
                  )}
                </td>
                <td className="px-4 py-3"><StatusBadge status={user.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="py-12 text-center text-gray-400 text-sm">
            <i className="ri-search-line text-2xl block mb-2" />
            Không tìm thấy kết quả
          </div>
        )}
      </div>
    </div>
  );
};
